import { useEffect, useState } from 'react';
import { useIsMounted, useLocalStorage, useDebounce } from 'usehooks-ts';
import { cloneDeep } from 'lodash';

import type {
  CountriesOption,
  getReferencesAutocomplete,
  getReferencesCountries,
  getReferencesCountriesResponse,
  GroupedOption,
  GroupedOptions,
  MetaregionsOption,
  PopularDirectionsOption,
} from '@lt/api/dist/methods/references/types';
import {
  DEFAULT_TRIPSTER_FULL_OPTIONS,
  DEFAULT_TRIPSTER_START_OPTIONS,
  SEARCH_TYPE_NAMES,
  TRIPSTER_API_PATH,
  storageKeys,
} from '../constants';
import type { SearchType } from '../types';
import { GroupedPreviousSearchesOption, PreviousSearchesOption } from '../context';
import { useGetApiClient } from '../../../context/apiContext';
import { fetchData } from '../utils/fetchData';

export function useAutocomplete(
  searchType: SearchType = SEARCH_TYPE_NAMES.PACKAGE,
  isWizard?: boolean
) {
  const [startOptions, setStartOptions] = useState<
    (GroupedPreviousSearchesOption | GroupedOption)[]
  >([]);
  const [fullOptions, setFullOptions] = useState<GroupedOptions>([]);
  const [foundGroupedOptions, setFoundGroupedOptions] = useState<GroupedOptions>([]);
  const [params, setParams] = useState<getReferencesAutocomplete>();
  const [departureValue, setDepartureValue] = useState<string>();
  const [loading, setLoading] = useState(false);
  const isMounted = useIsMounted();
  const debounceDepartureValue = useDebounce(departureValue, 100);
  const hasFromCityParam = searchType === SEARCH_TYPE_NAMES.PACKAGE && !isWizard;
  const [savedLandParams, setSavedLandParams] = useLocalStorage<PreviousSearchesOption[]>(
    storageKeys.landParams,
    []
  );
  const [allCountriesLookup, setAllCountriesLookup] =
    useState<Record<string, getReferencesCountriesResponse>>();
  const api = useGetApiClient();

  // добавляем groupName и преобразуем строку даты из локалстораджа в дату
  const formatDateSavedLandParams = (
    landParams: PreviousSearchesOption[]
  ): GroupedPreviousSearchesOption => {
    /**
     * Функция, которая проверяет подходят ли сохраненный поиск под новый формат флексовых дат
     * раньше флексовые даты выбирались плюсиком и можно было сделать разные флексы от каждой даты
     * сейчас у нас фиксированные +- 1 +- 2
     * После ab убрать при победе варианта со старыми флексами, если выиграет вариант B лучше перепилить и сделать одно отдельное поле под флексы
     * ВАЖНО: не мутировать localStorage до победы одного из вариантов
     * @see https://leveltravel.atlassian.net/browse/LT-36149
     */
    const isFitNewFormatFlexDates = (left: unknown, right: unknown): boolean => {
      if (
        left &&
        right &&
        typeof left === 'object' &&
        typeof right === 'object' &&
        'shift' in left &&
        'shift' in right &&
        left.shift &&
        typeof left.shift === 'object' &&
        'left' in left.shift &&
        'right' in left.shift &&
        right.shift &&
        typeof right.shift === 'object' &&
        'left' in right.shift &&
        'right' in right.shift
      ) {
        return (
          left.shift.left === right.shift.left &&
          left.shift.right === right.shift.right &&
          left.shift.left === right.shift.right
        );
      }
      return true;
    };
    const convertToNewFormatFlexDates = (shift: unknown, left: unknown): number => {
      if (shift && typeof shift === 'number') {
        return shift;
      }
      if (
        left &&
        typeof left === 'object' &&
        'shift' in left &&
        left.shift &&
        typeof left.shift === 'object' &&
        'left' in left.shift &&
        typeof left.shift.left === 'number'
      ) {
        return left.shift.left;
      }
      return 0;
    };
    const landParamsOptions = landParams
      .filter((item) => isFitNewFormatFlexDates(item.datepicker.left, item.datepicker.right))
      .map((item) => ({
        ...item,
        datepicker: {
          left: {
            date: new Date(item.datepicker.left.date ? item.datepicker.left.date : ''),
          },
          right: {
            date: new Date(item.datepicker.right.date ? item.datepicker.right.date : ''),
          },
          shift: convertToNewFormatFlexDates(item.datepicker.shift, item.datepicker.left),
          buttonsClicked: [],
          initialTab: null,
        },
      }));
    return {
      groupName: 'previousSearches',
      options: landParamsOptions,
    };
  };

  /**
   * Приводим данные страны Трипстера в формат страны ЛТ
   */
  const formatTripsterCountry = (
    country: any
  ): Omit<CountriesOption, 'groupName'> & { url: string; isTrip: boolean } => ({
    value: country.id,
    label: country.title.replaceAll(/<\/?em>/gm, ''),
    visa: '',
    closed: false,
    budget: 0,
    nameEn: '',
    nameRuForm3: '',
    url: country.url,
    isTrip: true,
  });

  /**
   * Приводим данные метарегиона Трипстера в формат метарегиона ЛТ
   */
  const formatTripsterMetaregion = (
    metaregion: any
  ): Omit<MetaregionsOption, 'groupName'> & { url: string; isTrip: boolean } => ({
    value: metaregion.id,
    label: metaregion.title.replaceAll(/<\/?em>/gm, ''),
    description: metaregion.country?.name_ru?.replaceAll(/<\/?em>/gm, ''),
    url: metaregion.url,
    isTrip: true,
  });

  /**
   * Заполняем массив опций стран:
   * в начальные опций записываем первые 3,
   * в полные - все по аналогии с тем, что возвращает referencesPlaces
   */
  const fillTripsterCountriesOptions = (
    tripsterStartOptions: GroupedOption[],
    tripsterFullOptions: GroupedOption[],
    countries: any
  ) => {
    tripsterStartOptions[0].fullOptionsCount = countries.length;
    countries.forEach((country: any, i: number) => {
      const formattedTripsterCountry = formatTripsterCountry(country);

      if (i < 3) {
        tripsterStartOptions[0].options.push({
          ...formattedTripsterCountry,
          groupName: 'popularCountries',
        });
      }
      tripsterFullOptions[0].options.push({
        ...formattedTripsterCountry,
        groupName: 'allCountries',
      });
    });
  };

  /**
   * Заполняем массив опций метарегионов:
   * в начальные опций записываем первые 3,
   * в полные - все  по аналогии с тем, что возвращает referencesPlaces
   */
  const fillTripsterMetaregionsOptions = (
    tripsterStartOptions: GroupedOption[],
    tripsterFullOptions: GroupedOption[],
    metaregions: any
  ) => {
    tripsterStartOptions[1].fullOptionsCount = metaregions.length;
    metaregions.forEach((metaregion: any, i: number) => {
      const formattedTripsterMetaregion = formatTripsterMetaregion(metaregion);

      if (i < 3) {
        tripsterStartOptions[1].options.push({
          ...formattedTripsterMetaregion,
          groupName: 'metaregions',
        });
      }
      tripsterFullOptions[1].options.push({
        ...formattedTripsterMetaregion,
        groupName: 'allMetaregions',
      });
    });
  };

  /**
   * Получаем направления Трипстера для поиска экскурсий
   * Страны и города с регионами запрашиваем отдельно, т.к.
   * без указания типа возвращаются только города
   */
  const getTripsterPlaces = () => {
    return Promise.all([
      fetch(`${TRIPSTER_API_PATH}?types=country`),
      fetch(`${TRIPSTER_API_PATH}?types=city,region`),
    ])
      .then(([countriesResponse, metaregionsResponse]) =>
        Promise.all([countriesResponse.json(), metaregionsResponse.json()])
      )
      .then(([countries, metaregions]) => {
        const tripsterStartOptions: GroupedOption[] = cloneDeep(DEFAULT_TRIPSTER_START_OPTIONS);
        const tripsterFullOptions: GroupedOption[] = cloneDeep(DEFAULT_TRIPSTER_FULL_OPTIONS);

        fillTripsterCountriesOptions(tripsterStartOptions, tripsterFullOptions, countries);
        fillTripsterMetaregionsOptions(tripsterStartOptions, tripsterFullOptions, metaregions);

        setStartOptions(tripsterStartOptions);
        setFullOptions(tripsterFullOptions);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Получаем направления ЛТ для поиска туров или отелей
  const getReferencesPlaces = (placesParams: getReferencesCountries) =>
    api
      ?.referencesPlaces(placesParams)
      // @todo
      .then((data) => {
        if (!isMounted()) return;

        const previousSearchesGroup = formatDateSavedLandParams(savedLandParams[searchType] || []);
        const popularDirections =
          (data?.popularDirections && data?.popularDirections[0].options) || [];

        // Мэтчим данные направления и страны
        if (allCountriesLookup) {
          popularDirections?.forEach((direction: PopularDirectionsOption, idx) => {
            const country = allCountriesLookup[direction.countryId];
            popularDirections[idx] = { ...direction, country };
          });
        }

        setStartOptions(
          savedLandParams && allCountriesLookup && data.popularDirections
            ? [previousSearchesGroup, ...data.startOptions, data.popularDirections[0]]
            : data.startOptions
        );
        setFullOptions(
          allCountriesLookup && data.popularDirections
            ? [...data.fullOptions, data.popularDirections[0]]
            : data.fullOptions
        );

        if (!popularDirections.length || allCountriesLookup) {
          setLoading(false);
        }
      });

  /**
   * Формируем урл для запроса автокомплита Трипстера,
   * когда пользователь ввёл что-то в поле направлений
   */
  const getTripsterQueryUrl = (queryWithoutQuotes: string) => {
    const queryParams = new URLSearchParams({
      query: queryWithoutQuotes,
      limit: '10',
      types: 'city, region, country',
    });

    return `${TRIPSTER_API_PATH}?${queryParams.toString()}`;
  };

  /**
   * Дёргаем автокомплит Трипстера, когда пользователь ввёл что-то
   * в поле выбора направления при поиске экскурсий
   */
  const getTripsterAutocomplete = (queryWithoutQuotes: string) => {
    const apiUrl = getTripsterQueryUrl(queryWithoutQuotes);

    fetch(apiUrl)
      .then((response) => response.json())
      .then((options) => {
        if (!isMounted()) return;

        const queryWithEm = `<em>${queryWithoutQuotes}</em>`;
        const isPerfectMatch = options[0] === queryWithEm;

        const preparedData: GroupedOptions = cloneDeep(DEFAULT_TRIPSTER_START_OPTIONS);
        options.forEach((option: any) => {
          if (option.type === 'country') {
            preparedData[0].options.push({
              ...formatTripsterCountry(option),
              groupName: 'popularCountries',
            });
          } else if (
            !isPerfectMatch ||
            (isPerfectMatch && option.country.name_ru === queryWithEm)
          ) {
            preparedData[1].options.push({
              ...formatTripsterMetaregion(option),
              groupName: 'metaregions',
            });
          }
        });
        setFoundGroupedOptions(preparedData);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Дёргаем автокомплит ЛТ, когда пользователь ввёл что-то
   * в поле выбора направления при поиске туров / отелей
   */
  const getLtAutocomplete = (queryWithoutQuotes: string) => {
    if (!params) return;

    const autocompleteParams =
      searchType === SEARCH_TYPE_NAMES.PACKAGE
        ? { query: queryWithoutQuotes, from_city: params.from_city, search_type: searchType }
        : { query: queryWithoutQuotes, search_type: searchType };

    fetchData(api?.referencesAutocompleteV2(autocompleteParams), autocompleteParams.query)
      ?.then((options) => {
        if (isMounted()) {
          setFoundGroupedOptions(options as (prevState: GroupedOptions) => GroupedOptions);
        }
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    api?.getReferencesCountries({}).then((data) => {
      if (!data) return;

      const allCountriesLookup = data.reduce(
        (countriesLookup, country) => ({
          ...countriesLookup,
          [country.id]: country,
        }),
        {}
      );

      setAllCountriesLookup(allCountriesLookup);
    });
  }, [api]);

  useEffect(() => {
    const savedLandParamsByType = savedLandParams[searchType] || [];
    const previousSearchesGroup = formatDateSavedLandParams(savedLandParamsByType);

    const filterStartOptions = startOptions.slice(1);

    setStartOptions([previousSearchesGroup, ...filterStartOptions]);
  }, [savedLandParams, searchType]);

  useEffect(() => {
    try {
      setLoading(true);

      if (searchType === SEARCH_TYPE_NAMES.TRIP) {
        if (!isMounted()) return;
        getTripsterPlaces();
      } else {
        const placesParams = {
          search_type: searchType,
          from_city: hasFromCityParam ? debounceDepartureValue : undefined,
        };
        getReferencesPlaces(placesParams);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted, debounceDepartureValue, searchType, allCountriesLookup]);

  useEffect(() => {
    const queryWithoutQuotes = params?.query?.replace(/['"]/g, '');
    if (params && queryWithoutQuotes) {
      setLoading(true);

      if (searchType === SEARCH_TYPE_NAMES.TRIP) {
        getTripsterAutocomplete(queryWithoutQuotes);
        return;
      }

      getLtAutocomplete(queryWithoutQuotes);
    }
  }, [isMounted, params, searchType]);

  return {
    startOptions,
    fullOptions,
    foundGroupedOptions,
    setParams,
    loading,
    setDepartureValue,
    savedLandParams,
    setSavedLandParams,
  };
}
